// templates/SinglePostTemplate/sections/SinglePostTemplateSection.tsx

import React, { useEffect, useState } from "react";

/* ============================================
   PALETTE VERT OLIVE / BLANC CASSÉ
   ============================================ */

const colors = {
  oliveDark: "#556B2F",
  oliveMedium: "#6B8E23",
  oliveLight: "#8FBC8F",
  offWhite: "#FAF9F6",
  text: "#2F2F2F",
  textSecondary: "#6B6B6B",
  white: "#FFFFFF",
  border: "#E0DDD5",
};

function stripHtml(html: string): string {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

function estimateReadingTime(html: string): number {
  const words = stripHtml(html).trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getSlugFromURL(): string {
  const path = window.location.pathname.replace(/\/+$/, "").replace(/^\/+/, "");
  const segments = path.split("/").filter(Boolean);
  return segments.length > 0 ? segments[segments.length - 1] : "";
}

declare const wvcClient: any;

const SinglePostTemplateSection: React.FC = () => {
  const [post, setPost] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const slug = getSlugFromURL();

    if (!slug) {
      setIsError(true);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setIsError(false);

    const fetchPost = async () => {
      // Méthode 1 : wvcClient (10Web)
      try {
        if (typeof wvcClient !== "undefined" && wvcClient?.get_wp_query_results) {
          const res = await wvcClient.get_wp_query_results(
            { post_type: "post", name: slug, posts_per_page: 1 },
            { embeds: ["author", "wp:featuredmedia", "wp:term"], fields: [] },
            false
          );
          if (res?.posts?.length > 0) {
            setPost(res.posts[0]);
            setIsLoading(false);
            return;
          }
        }
      } catch (_) {}

      // Méthode 2 : API REST WordPress par slug
      try {
        const res = await fetch(
          `https://effet-conseil.com/wp-json/wp/v2/posts?slug=${encodeURIComponent(slug)}&_embed`
        );
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setPost(data[0]);
            setIsLoading(false);
            return;
          }
        }
      } catch (_) {}

      setIsError(true);
      setIsLoading(false);
    };

    fetchPost();
  }, []);

  /* --- Loading --- */
  if (isLoading) {
    return (
      <div
        style={{
          backgroundColor: colors.offWhite,
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: `4px solid ${colors.border}`,
            borderTopColor: colors.oliveMedium,
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  /* --- Error --- */
  if (isError || !post) {
    return (
      <div
        style={{
          backgroundColor: colors.offWhite,
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <h2 style={{ color: colors.oliveDark, margin: 0 }}>Article introuvable</h2>
        <p style={{ color: colors.textSecondary, margin: 0 }}>
          Cet article n'existe pas ou a été supprimé.
        </p>
        <a
          href="/"
          style={{
            color: colors.white,
            backgroundColor: colors.oliveMedium,
            padding: "10px 24px",
            borderRadius: 6,
            textDecoration: "none",
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          ← Retour à l'accueil
        </a>
      </div>
    );
  }

  /* --- Data extraction --- */
  const title = post.title?.rendered || post.title || post.post_title || "Sans titre";
  const content = post.content?.rendered || post.content || post.post_content || "";
  const excerpt = post.excerpt?.rendered || post.excerpt || post.post_excerpt || "";
  const dateFormatted = (post.date || post.post_date) ? formatDate(post.date || post.post_date) : "";
  const readingTime = estimateReadingTime(content);

  const featuredImage =
    post._embedded?.["wp:featuredmedia"]?.[0]?.source_url ||
    post.featured_media_url ||
    post.featuredImage ||
    null;

  const authorName =
    post._embedded?.author?.[0]?.name ||
    post.author?.name ||
    "Effet Conseil";

  const termGroups = post._embedded?.["wp:term"];
  const categories: string[] = Array.isArray(termGroups)
    ? termGroups.flat().filter((t: any) => t?.taxonomy === "category").map((t: any) => t.name)
    : [];
  const categoryLabel = categories.length > 0 ? categories.join(", ") : "Actualités";

  /* --- Render --- */
  return (
    <article style={{ backgroundColor: colors.offWhite, minHeight: "100vh" }}>
      {/* ====== HERO BANNER ====== */}
      <header
        style={{
          background: `linear-gradient(135deg, ${colors.oliveDark} 0%, ${colors.oliveMedium} 100%)`,
          padding: "80px 24px 60px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -60,
            right: -60,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.06)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            display: "inline-block",
            backgroundColor: "rgba(255,255,255,0.15)",
            color: colors.white,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            padding: "6px 18px",
            borderRadius: 20,
            marginBottom: 20,
          }}
        >
          {categoryLabel}
        </div>

        <h1
          style={{
            color: colors.white,
            fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
            fontWeight: 800,
            lineHeight: 1.25,
            maxWidth: 800,
            margin: "0 auto 24px",
          }}
          dangerouslySetInnerHTML={{ __html: title }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 24,
            flexWrap: "wrap",
            color: "rgba(255,255,255,0.85)",
            fontSize: 14,
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
              <path d="M4 20c0-3.3 3.6-6 8-6s8 2.7 8 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            {authorName}
          </span>
          {dateFormatted && (
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                <path d="M3 10h18" stroke="currentColor" strokeWidth="2" />
                <path d="M8 2v4M16 2v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              {dateFormatted}
            </span>
          )}
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
              <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            {readingTime} min de lecture
          </span>
        </div>
      </header>

      {/* ====== FEATURED IMAGE ====== */}
      {featuredImage && (
        <div
          style={{
            maxWidth: 900,
            margin: "-40px auto 0",
            padding: "0 24px",
            position: "relative",
            zIndex: 2,
          }}
        >
          <img
            src={featuredImage}
            alt=""
            style={{
              width: "100%",
              maxHeight: 460,
              objectFit: "cover",
              borderRadius: 12,
              boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
            }}
          />
        </div>
      )}

      {/* ====== ARTICLE BODY ====== */}
      <div style={{ maxWidth: 780, margin: "0 auto", padding: "48px 24px 80px" }}>
        {excerpt && (
          <div
            style={{
              borderLeft: `4px solid ${colors.oliveMedium}`,
              paddingLeft: 20,
              marginBottom: 40,
              color: colors.textSecondary,
              fontSize: 17,
              fontStyle: "italic",
              lineHeight: 1.7,
            }}
            dangerouslySetInnerHTML={{ __html: excerpt }}
          />
        )}

        <div
          className="single-post-content"
          style={{ color: colors.text, fontSize: 17, lineHeight: 1.8 }}
          dangerouslySetInnerHTML={{ __html: content }}
        />

        <div
          style={{
            marginTop: 56,
            paddingTop: 32,
            borderTop: `1px solid ${colors.border}`,
            textAlign: "center",
          }}
        >
          <a
            href="/"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              backgroundColor: colors.oliveMedium,
              color: colors.white,
              padding: "12px 32px",
              borderRadius: 8,
              textDecoration: "none",
              fontWeight: 600,
              fontSize: 15,
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) =>
              ((e.target as HTMLElement).style.backgroundColor = colors.oliveDark)
            }
            onMouseLeave={(e) =>
              ((e.target as HTMLElement).style.backgroundColor = colors.oliveMedium)
            }
          >
            ← Retour à l'accueil
          </a>
        </div>
      </div>

      {/* ====== STYLES ====== */}
      <style>{`
        .single-post-content h2 { color: ${colors.oliveDark}; font-size: 1.6rem; font-weight: 700; margin: 40px 0 16px; padding-bottom: 8px; border-bottom: 2px solid ${colors.oliveLight}; }
        .single-post-content h3 { color: ${colors.oliveMedium}; font-size: 1.3rem; font-weight: 700; margin: 32px 0 12px; }
        .single-post-content h4 { color: ${colors.oliveDark}; font-size: 1.1rem; font-weight: 600; margin: 24px 0 8px; }
        .single-post-content p { margin: 0 0 18px; }
        .single-post-content a { color: ${colors.oliveMedium}; text-decoration: underline; text-underline-offset: 3px; transition: color 0.2s; }
        .single-post-content a:hover { color: ${colors.oliveDark}; }
        .single-post-content ul, .single-post-content ol { padding-left: 24px; margin: 0 0 18px; }
        .single-post-content li { margin-bottom: 8px; }
        .single-post-content blockquote { border-left: 4px solid ${colors.oliveMedium}; margin: 24px 0; padding: 16px 20px; background: rgba(107,142,35,0.06); border-radius: 0 8px 8px 0; font-style: italic; color: ${colors.textSecondary}; }
        .single-post-content img { max-width: 100%; height: auto; border-radius: 8px; margin: 24px 0; }
        .single-post-content table { width: 100%; border-collapse: collapse; margin: 24px 0; font-size: 15px; }
        .single-post-content th { background: ${colors.oliveDark}; color: ${colors.white}; padding: 12px 16px; text-align: left; font-weight: 600; }
        .single-post-content td { padding: 10px 16px; border-bottom: 1px solid ${colors.border}; }
        .single-post-content tr:nth-child(even) td { background: rgba(107,142,35,0.04); }
        .single-post-content strong { color: ${colors.oliveDark}; }
        .single-post-content .wp-block-separator, .single-post-content hr { border: none; border-top: 2px solid ${colors.border}; margin: 32px 0; }
        @media (max-width: 640px) { .single-post-content h2 { font-size: 1.35rem; } .single-post-content h3 { font-size: 1.15rem; } }
      `}</style>
    </article>
  );
};

export default SinglePostTemplateSection;
